/**
 * MDMS-driven RewampAppBar nav, with the local hardcoded list
 * (getRewampRoleBasedModules) kept as a full fallback at every stage:
 *
 *   0. Logged-out visitors have no real role to scope an MDMS query by —
 *      there's no "guest" role in the backend's role catalogue — so MDMS is
 *      never queried for them; they always render the local hardcoded list.
 *   1. Fetch Homepage.module (module master, bulk — small catalogue, no
 *      per-role variance) + Homepage.role-module grants, but the latter is
 *      fetched ONE SERVER-FILTERED CALL PER ROLE the logged-in user holds
 *      (login's user-info response is what getUserRoles() reads), via
 *      MdmsCriteria.filters: { role }, rather than pulling the whole table
 *      and filtering client-side.
 *   2. Union the grants across every role the user holds (a user can carry
 *      more than one), keeping the smallest displayOrder when two held
 *      roles both grant the same code.
 *   3. If the user's actual role(s) have no grants at all, fire one more
 *      filtered call for the EMPLOYEE bucket before giving up on MDMS.
 *   4. If MDMS is still loading, errored, or has nothing usable at any of
 *      the above steps, use the local hardcoded list untouched.
 *
 * Home is deliberately NEVER sourced from MDMS — nobody has seeded it there
 * (no role has a Home grant), and its logged-out-vs-logged-in path swap
 * (`/` vs `/home`) is auth-state, not module content. It's always
 * synthesized here and prepended first, regardless of which path above
 * resolves the rest of the list.
 *
 * Caching: react-query's own cache (staleTime: Infinity) only lives for the
 * current SPA session — a hard reload wipes it, which was refetching all 3
 * calls on every refresh. RESP_CACHE_TTL_SECONDS backs every fetch with a
 * PersistantStorage (localStorage) entry that expires after 60s, so a
 * refresh within that window reuses the cached response instead of hitting
 * the API again; past 60s it refetches. Critically, what gets cached is the
 * RAW bilingual row (both titleEn and titleHi together), never a
 * language-resolved title — the EN/HI choice always happens live in the
 * useMemo below, off the live i18n.language. That's what keeps a language
 * switch instant and correct regardless of whether the underlying rows came
 * from the network or from the 60s cache; caching a pre-resolved title would
 * have baked the language in and required a hard refresh to update it.
 *
 * Ordering: primary key is displayOrder; ties (two DIFFERENT codes sharing
 * the same displayOrder — expected when the union spans multiple roles,
 * since each role's list is numbered independently) break alphabetically by
 * the module's own `code`, e.g.
 *   role-1 grants: [1a, 2a, 3, 4]   role-2 grants: [1b, 2b]
 *   union renders: [1a, 1b, 2a, 2b, 3, 4]
 * The tie-break is the raw code, NOT the localized title — that keeps
 * ordering identical and deterministic across a language switch instead of
 * reshuffling ties whenever EN/HI labels sort differently. This ordering is
 * computed once here into a single flat `navItems` array; RewampMainNav then
 * only *filters* that array by `showFrom` breakpoint into inline vs. "More"
 * — Array.filter preserves relative order, so the same
 * displayOrder→alphabetical-code ordering is enforced inside the More
 * overflow menu too, with no separate sort there.
 */
import { useMemo } from "react";
import { useQuery, useQueries } from "react-query";
import { useTranslation } from "react-i18next";
import { MdmsService } from "../../../services/MDMS";
import { PersistantStorage } from "../../../Utils/LocalStorage";
import { getUserRoles } from "../../../components/Utils";
import { ROLES } from "../../routes/roleGroups";
import { getRewampRoleBasedModules, SHOW_FROM_BY_INDEX } from "../RewampUtils";

const MODULE_SCHEMA = "Homepage.module";
const ROLE_MODULE_SCHEMA = "Homepage.role-module";
const FALLBACK_ROLE = ROLES.EMPLOYEE;
const RESP_CACHE_TTL_SECONDS = 60;

// Legacy i18n keys, reused ONLY when a module's own titleEn/titleHi both
// come back empty (bad/partial CMS entry) — strengthens localization so a
// bad row degrades to an already-translated key instead of the raw code
// slug ("SCHEME"), and stays fully reactive to language switches since
// RewampMainNav's t(item.title) re-resolves a real key live either way.
const LEGACY_TITLE_KEY_BY_CODE = {
  SCHEME: "DFSWEB_SCHEMES",
  ADVISORY: "REWAMP_NAV_ADVISORIES",
  KNOWLEDGE: "REWAMP_NAV_KNOWLEDGE",
  HELP: "DFSWEB_HELP",
  TRACK: "DFSWEB_TRACK",
  FARMER_REGISTRATION: "REWAMP_NAV_FARMER_REGISTRATION",
  DASHBOARD: "DFSWEB_DASHBOARD",
  DSS_DASHBOARD: "DFSWEB_DASHBOARD",
  CAROUSAL: "DFSWEB_CAROUSEL",
  DATA_REVIEW: "DFSWEB_DATA_REVIEW",
  GRM: "DFSWEB_GRM",
};

const buildHomeItem = () => {
  const isLoggedIn = localStorage.getItem("DfsWeb.isLoggedIn") === "true";
  return {
    title: "DFSWEB_HOME",
    path: isLoggedIn ? `${window.contextPath}/home` : `${window.contextPath}/`,
    showFrom: "md",
  };
};

// Only meaningful for logged-in users — guests never reach an MDMS query
// (see useAppbarRoleModules), so there's no "no role" case to resolve here.
const resolveEffectiveRoles = () => {
  const roles = getUserRoles();
  return roles?.length ? roles : [FALLBACK_ROLE];
};

// cta → {path, key, comingSoon}. Defensively normalizes a path missing its
// leading "/" (seen live on one row) instead of producing a broken URL.
const decodeCta = (rawCta) => {
  if (!rawCta) return { path: null };
  if (rawCta === "COMING_SOON") return { path: null, comingSoon: true };
  if (rawCta.startsWith("#")) return { path: null, key: rawCta };
  const normalized = rawCta.startsWith("/") ? rawCta : `/${rawCta}`;
  return { path: `${window.contextPath}${normalized}` };
};

// PersistantStorage.get() already returns null on a missing OR expired
// entry (see Utils/LocalStorage.js), so a plain truthiness check correctly
// tells a valid cached [] apart from "nothing cached" without extra bookkeeping.
const fetchCached = async (cacheKey, fetcher) => {
  const cached = PersistantStorage.get(cacheKey);
  if (cached) return cached;
  const fresh = await fetcher();
  PersistantStorage.set(cacheKey, fresh, RESP_CACHE_TTL_SECONDS);
  return fresh;
};

const fetchModuleRows = (tenantId) =>
  fetchCached(`appbarModuleMaster.${tenantId}`, async () => {
    const response = await MdmsService.mdmsV2Call(tenantId, MODULE_SCHEMA);
    return response?.data?.mdms ?? [];
  });

const fetchRoleModuleRows = (tenantId, role) =>
  fetchCached(`appbarRoleModuleMap.${tenantId}.${role}`, async () => {
    const response = await MdmsService.mdmsV2Call(tenantId, ROLE_MODULE_SCHEMA, { filters: { role } });
    return response?.data?.mdms ?? [];
  });

// Union a batch of already role-scoped grant rows against the module master:
// dedup by code (keeping the smallest displayOrder on overlap), then sort by
// displayOrder asc with code asc as the tie-break — deterministic and
// language-independent, so it holds for both inline items and whatever
// spills into the "More" overflow menu (RewampMainNav only filters this
// array by breakpoint, it never re-sorts).
const resolveNavRows = (rows, moduleByCode) => {
  const byCode = new Map();
  rows
    .filter((row) => !row?.data?.isDeleted)
    .forEach((row) => {
      const code = row?.data?.code;
      const module = moduleByCode.get(code);
      if (!module) return; // grant references a module the master doesn't have
      const order = row?.data?.displayOrder ?? 999;
      const existing = byCode.get(code);
      if (!existing || order < existing._order) {
        byCode.set(code, { code, module, _order: order });
      }
    });
  return [...byCode.values()].sort(
    (a, b) => a._order - b._order || a.code.localeCompare(b.code)
  );
};

export const useAppbarRoleModules = () => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === "en_IN" || i18n.language === "en";
  const tenantId = PersistantStorage.get("tenantId") || "br";
  const isLoggedIn = localStorage.getItem("DfsWeb.isLoggedIn") === "true";
  const effectiveRoles = isLoggedIn ? resolveEffectiveRoles() : [];

  const { data: moduleRows, isLoading: isModuleLoading } = useQuery(
    ["homepageModuleMaster", tenantId],
    () => fetchModuleRows(tenantId),
    { staleTime: Infinity, retry: 2, enabled: isLoggedIn }
  );

  // One server-filtered call per role the user actually holds.
  const primaryQueries = useQueries(
    effectiveRoles.map((role) => ({
      queryKey: ["homepageRoleModuleMap", tenantId, role],
      queryFn: () => fetchRoleModuleRows(tenantId, role),
      staleTime: Infinity,
      retry: 2,
      enabled: isLoggedIn,
    }))
  );
  const isPrimaryLoading = primaryQueries.some((q) => q.isLoading);
  const primaryRows = primaryQueries.flatMap((q) => (Array.isArray(q.data) ? q.data : []));

  // Generic default: only fires once the primary role(s) have settled empty,
  // and only if EMPLOYEE wasn't already one of the primary roles fetched above.
  const needsFallbackFetch =
    isLoggedIn && !isPrimaryLoading && primaryRows.length === 0 && !effectiveRoles.includes(FALLBACK_ROLE);
  const { data: fallbackRoleRows, isLoading: isFallbackLoading } = useQuery(
    ["homepageRoleModuleMap", tenantId, FALLBACK_ROLE],
    () => fetchRoleModuleRows(tenantId, FALLBACK_ROLE),
    { staleTime: Infinity, retry: 2, enabled: needsFallbackFetch }
  );

  return useMemo(() => {
    const localFallback = getRewampRoleBasedModules();
    const homeItem = buildHomeItem();

    // No real role to scope an MDMS query by — skip MDMS entirely.
    if (!isLoggedIn) return localFallback;

    if (isModuleLoading || isPrimaryLoading) return localFallback;
    if (!Array.isArray(moduleRows) || !moduleRows.length) return localFallback;
    if (needsFallbackFetch && isFallbackLoading) return localFallback;

    const moduleByCode = new Map(
      moduleRows
        .filter((row) => !row?.data?.isDeleted)
        .map((row) => [row?.data?.code, row?.data])
    );

    let resolved = resolveNavRows(primaryRows, moduleByCode);
    if (!resolved.length && Array.isArray(fallbackRoleRows)) {
      resolved = resolveNavRows(fallbackRoleRows, moduleByCode);
    }

    if (!resolved.length) return localFallback;

    // resolved is already ordered (displayOrder asc, code asc tie-break) by
    // resolveNavRows — this map only resolves each code into a render-ready
    // item, it must not touch the ordering. showFrom is keyed by the item's
    // FINAL position (index + 1, since Home occupies index 0), not by code —
    // see SHOW_FROM_BY_INDEX in RewampUtils.js for why: a per-code breakpoint
    // would desync from the real sort position once roles are unioned, so
    // inline/"More" is guaranteed to stay a continuous prefix/suffix of this
    // already-sorted list.
    const mdmsItems = resolved.map(({ code, module }, index) => {
      const { path, key, comingSoon } = decodeCta(module.cta);
      const legacyKey = LEGACY_TITLE_KEY_BY_CODE[code] ?? code;
      const title = isEnglish
        ? module.titleEn || legacyKey
        : module.titleHi || module.titleEn || legacyKey;
      return {
        title,
        path,
        ...(key && { key }),
        ...(comingSoon && { comingSoon }),
        showFrom: SHOW_FROM_BY_INDEX[index + 1] ?? "xl",
      };
    });

    return [homeItem, ...mdmsItems];
  }, [
    isLoggedIn,
    moduleRows,
    isModuleLoading,
    isPrimaryLoading,
    primaryRows,
    needsFallbackFetch,
    isFallbackLoading,
    fallbackRoleRows,
    isEnglish,
  ]);
};

export default useAppbarRoleModules;
